/**
 * Customer Subscribe Action (Newsletter/Marketing)
 * Triggered by: arc:action:customerSubscribe
 */

export const runCustomerSubscribe = async (data = {}) => {
    try {
        const { 
            email,
            firstName = '',
            lastName = '',
            phone = '',
            source = 'newsletter',
            tags = [],
            form,
            redirectOnSuccess = false,
            successMessage = 'Thank you for subscribing!'
        } = data;

        if (!email) {
            throw new Error('Email address is required');
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            throw new Error('Please enter a valid email address');
        }

        // Dispatch subscription attempt event
        window.dispatchEvent(new CustomEvent('customer:subscriptionAttempt', {
            detail: {
                email,
                firstName,
                lastName,
                phone,
                source,
                tags
            }
        }));

        // Prepare form data for Shopify newsletter signup
        const formData = new FormData();
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');
        formData.append('contact[email]', email);
        formData.append('contact[accepts_marketing]', '1');
        
        if (firstName) formData.append('contact[first_name]', firstName);
        if (lastName) formData.append('contact[last_name]', lastName);
        if (phone) formData.append('contact[phone]', phone);
        
        // Add tags for source tracking
        const allTags = ['newsletter', source, ...tags].filter(tag => tag);
        if (allTags.length > 0) {
            formData.append('contact[tags]', allTags.join(','));
        }

        // Submit subscription request
        const response = await fetch('/contact', {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check response type
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // Handle JSON response
                const responseData = await response.json();
                
                if (responseData.errors) {
                    const errorMessages = Object.values(responseData.errors).flat();
                    throw new Error(errorMessages.join(', '));
                }
            } else {
                // Handle HTML response - check for errors
                const responseText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(responseText, 'text/html');
                
                // Look for error messages
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    throw new Error(errors.join(', '));
                }
            }

            // Success
            window.dispatchEvent(new CustomEvent('customer:subscriptionSuccess', {
                detail: {
                    email,
                    firstName,
                    lastName,
                    phone,
                    source,
                    tags: allTags,
                    message: successMessage,
                    timestamp: Date.now()
                }
            }));

            if (redirectOnSuccess && typeof redirectOnSuccess === 'string') {
                window.location.href = redirectOnSuccess;
            }

            return {
                success: true,
                email,
                source,
                message: successMessage
            };

        } else {
            // Handle HTTP errors
            let errorMessage = 'Subscription failed';
            
            try {
                const errorText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    errorMessage = errors.join(', ');
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Subscribe error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:subscriptionError', {
            detail: {
                error: error.message,
                email: data.email,
                source: data.source
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize newsletter subscription forms
export const initializeCustomerSubscribe = (config = {}) => {
    const {
        formSelector = '[data-newsletter-form]',
        emailSelector = '[name="contact[email]"], [name="email"]',
        submitSelector = '[type="submit"]',
        autoSetupForms = true,
        enableEmailValidation = true
    } = config;

    if (autoSetupForms) {
        setupSubscriptionForms(formSelector, emailSelector, submitSelector, enableEmailValidation);
    }

    window.dispatchEvent(new CustomEvent('customerSubscribe:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup subscription form event listeners
const setupSubscriptionForms = (formSelector, emailSelector, submitSelector, enableEmailValidation) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleSubscriptionSubmit);
        form.addEventListener('submit', handleSubscriptionSubmit);

        if (enableEmailValidation) {
            const emailField = form.querySelector(emailSelector);
            if (emailField) {
                emailField.removeEventListener('blur', handleEmailValidation);
                emailField.addEventListener('blur', handleEmailValidation);
            }
        }
    });
};

// Handle subscription form submissions
const handleSubscriptionSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const email = formData.get('contact[email]') || formData.get('email');
    const firstName = formData.get('contact[first_name]') || formData.get('first_name');
    const lastName = formData.get('contact[last_name]') || formData.get('last_name');
    const phone = formData.get('contact[phone]') || formData.get('phone');
    
    // Get source from form data attribute or default
    const source = form.dataset.source || 'newsletter';
    
    // Get tags from form data attribute
    const tagsString = form.dataset.tags || '';
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Subscribing...';
    }

    try {
        const result = await window.dispatchEvent(new CustomEvent('arc:action:customerSubscribe', {
            detail: {
                email,
                firstName,
                lastName,
                phone,
                source,
                tags,
                form,
                redirectOnSuccess: form.dataset.redirectOnSuccess,
                successMessage: form.dataset.successMessage
            }
        }));

        // Clear form on success
        if (result?.success) {
            form.reset();
        }

    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Handle email validation
const handleEmailValidation = (event) => {
    const email = event.target.value;
    const isValid = validateEmail(email);
    
    window.dispatchEvent(new CustomEvent('customer:emailValidation', {
        detail: {
            email,
            isValid,
            field: event.target
        }
    }));
};

// Email validation helper
const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Unsubscribe functionality
export const runCustomerUnsubscribe = async (data = {}) => {
    try {
        const { 
            email,
            token,
            customerId
        } = data;

        if (!email && !token && !customerId) {
            throw new Error('Email, token, or customer ID is required for unsubscribe');
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');
        formData.append('contact[accepts_marketing]', '0');
        
        if (email) formData.append('contact[email]', email);
        if (token) formData.append('token', token);

        // Dispatch unsubscribe attempt event
        window.dispatchEvent(new CustomEvent('customer:unsubscribeAttempt', {
            detail: { email, token, customerId }
        }));

        // Submit unsubscribe request
        const response = await fetch('/contact', {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            window.dispatchEvent(new CustomEvent('customer:unsubscribeSuccess', {
                detail: {
                    email,
                    timestamp: Date.now()
                }
            }));

            return {
                success: true,
                email,
                message: 'Successfully unsubscribed from newsletter'
            };
        } else {
            throw new Error('Unsubscribe failed');
        }

    } catch (error) {
        window.dispatchEvent(new CustomEvent('customer:unsubscribeError', {
            detail: {
                error: error.message,
                email: data.email
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize unsubscribe forms
export const initializeCustomerUnsubscribe = (config = {}) => {
    const {
        formSelector = '[data-unsubscribe-form]',
        autoSetupForms = true
    } = config;

    if (autoSetupForms) {
        setupUnsubscribeForms(formSelector);
    }
};

// Setup unsubscribe form listeners
const setupUnsubscribeForms = (formSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleUnsubscribeSubmit);
        form.addEventListener('submit', handleUnsubscribeSubmit);
    });
};

// Handle unsubscribe form submissions
const handleUnsubscribeSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const email = formData.get('email');
    const token = formData.get('token');
    const customerId = form.dataset.customerId;

    await window.dispatchEvent(new CustomEvent('arc:action:customerUnsubscribe', {
        detail: {
            email,
            token,
            customerId
        }
    }));
};

export default runCustomerSubscribe;
